import React, { useEffect, useRef } from 'react';
import './App.css';
import ViaviLogo from './ViaviLogo';

function App() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    // Partiklerne lever på tværs af resize. Tidligere blev hele effekten kørt
    // forfra ved hver ændring af windowSize, og da animate() aldrig blev
    // aflyst, kørte den gamle løkke videre ved siden af den nye. På mobil
    // ændrer window.innerHeight sig hver gang adresselinjen folder sig ind
    // eller ud — altså ved næsten hver scroll — så løkkerne hobede sig op
    // indtil siden gik i stå. Nu sætter vi op ÉN gang og lader en
    // resize-handler justere lærredet uden at røre React-state.
    let particles = [];
    let connectionDistance = 150;

    const seed = () => {
      const count = Math.min(Math.floor(canvas.width * 0.1), 150);
      connectionDistance = Math.min(canvas.width * 0.12, 150);
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
        });
      }
    };

    // Kun bredden må så partiklerne om. Højden ændrer sig konstant på mobil
    // uden at der reelt er sket noget, og en gensåning ved hver scroll er
    // både synlig for brugeren og dyr.
    let lastWidth = -1;
    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
      if (Math.abs(w - lastWidth) > 1) {
        lastWidth = w;
        seed();
      }
    };
    resize();

    let resizeTimer = null;
    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 150);
    };
    window.addEventListener('resize', onResize);

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cd = connectionDistance;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        if (!reduceMotion) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
          if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(80, 200, 240, 0.5)';
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];

          // Billig forkastelse før kvadratroden. De fleste par er langt fra
          // hinanden, og to sammenligninger er markant billigere end et
          // Math.sqrt pr. par — ved 150 partikler er der 11.175 par pr. billede.
          const dx = p.x - p2.x;
          if (dx > cd || dx < -cd) continue;
          const dy = p.y - p2.y;
          if (dy > cd || dy < -cd) continue;

          const d2 = dx * dx + dy * dy;
          if (d2 >= cd * cd) continue;

          const distance = Math.sqrt(d2);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.strokeStyle = `rgba(80, 200, 240, ${1 - distance / cd})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    };

    let rafId = null;
    const animate = () => {
      draw();
      rafId = requestAnimationFrame(animate);
    };

    if (reduceMotion) {
      draw(); // ét stillbillede, ingen løbende animation
    } else {
      rafId = requestAnimationFrame(animate);
    }

    // Oprydningen er hele pointen: uden den overlever løkken sin egen effekt.
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="App">
      <canvas ref={canvasRef} className="canvas-bg" />
      <div className="content">
        <ViaviLogo />
        <p className="tagline">Engineered in React, deployed by <strong>viavi.dk</strong> — reliable, scalable, cloud-native.</p>
        <p className="footer">Jimmi Frederiksen</p>
      </div>
    </div>
  );
}

export default App;
